import { retry } from '@app/utils/retry'
import { sleep } from '@app/utils/sleep'

import { dispose } from '@app/packages/disposable/disposable'
import { spawn } from '@app/packages/disposable/spawn'
import type { TrackableValue } from '@app/packages/reactives/TrackableValue'

import { ChatServerRateLimitError, ChatServerRestError } from './RestClient'
import {
  type ChatServerSendOptions,
  type ChatServerSendResult,
  ChatServerSocketClient,
  type ChatServerSocketEvent,
  type ChatServerSocketListener,
  type ChatServerToken,
  type SocketFactory,
} from './SocketClient'
import type { ChatServerAction } from './state'
import { setConnected, setConnectFailed } from './state'
import type { ChatServerMessage } from './types'

export type ChatServerManagerOptions = {
  // --- socket wiring (forwarded to the package socket client) ---
  getWsUrl: () => string | null
  getToken: () => Promise<ChatServerToken>
  /** Synchronous read of the freshest access token, used to re-authenticate Phoenix's own reconnects. */
  peekToken?: () => string | null
  /** The server's clock, to judge a token's `exp` by; this device's when absent. */
  serverNow?: () => number
  /** Current UI locale for the socket `locale` connect param. */
  getLocale?: () => string | null
  getSelfUid: () => string | null
  /** Whether the user has the app in front of them; the server counts them online only then. Foreground when omitted. */
  foreground?: TrackableValue<boolean>
  /** Sink for normalized state updates (the host bridges this into its store). */
  dispatch: (action: ChatServerAction) => void
  socketFactory?: SocketFactory
  log?: (...args: unknown[]) => void
  /** Report a caught background error to the host (e.g. Sentry); receives a stable `op` tag. */
  reportError?: (error: unknown, context: { op: string }) => void
  /** Whether the device has a network right now: a connect that fails without one says nothing about chat_server, so it is not counted towards the report. Online when omitted. */
  isOnline?: () => boolean

  // --- host lifecycle policy (all optional; the package stays host-agnostic) ---
  /** Resolve once chat should connect for the current user (readiness gate). */
  waitUntilReady?: (signal: AbortSignal) => Promise<void>
  /** Final gate checked after readiness — return false to skip connecting (e.g. supervised/polygon). */
  shouldConnect?: () => boolean
  /** Register the live manager with the host (e.g. a context); the returned `Disposable` is torn down with the lifecycle. */
  register?: (manager: ChatServerManager) => Disposable
  /** Run when the manager becomes active (connected-intent established); the returned `Disposable` is torn down with the lifecycle. */
  onActivate?: () => Disposable
  /** Load the user's initial channel list once connected. */
  loadInitialChannels?: (signal: AbortSignal) => Promise<void>
  /** Fetch a channel the host doesn't know yet (a message arrived for an unknown dialog). */
  ensureChannelLoaded?: (channelId: string) => Promise<void>
  /** Reload a channel the host already holds (its participants changed); unknown channels are ignored. */
  refreshChannel?: (channelId: string) => Promise<void>
  /** Whether a channel is currently on-screen — suppresses notification for it. */
  isChannelActive?: (channelId: string) => boolean
  /** Notify the user of an incoming message (sound, push, badge — the host decides). */
  onNotify?: (message: ChatServerMessage, channelId: string) => void
}

export type ChatServerManagerEvent = ChatServerSocketEvent
export type ChatServerManagerListener = ChatServerSocketListener

/**
 * Host-agnostic orchestrator of the package socket client. Constructed with
 * injected bindings (socket wiring + lifecycle policy), it wires incoming
 * messages to the host (channel backfill + notification) and, on `start()`,
 * runs the connect lifecycle: register → wait for readiness → gate → activate →
 * connect-with-retry → load initial channels. The host owns the returned
 * `Disposable`; disposing it tears the whole lifecycle down.
 */
export class ChatServerManager {
  private readonly options: ChatServerManagerOptions
  private readonly socket: ChatServerSocketClient
  /** The connect attempt in hand — `start()`'s first, or a rebuild's — or its wait to retry; null once the socket is up. */
  private connectAttempt: AbortController | null = null
  /** The loop bringing a retired socket back; `disconnect()` cancels it. */
  private rebuilding: Disposable | null = null

  constructor(options: ChatServerManagerOptions) {
    this.options = options
    this.socket = new ChatServerSocketClient({
      getWsUrl: options.getWsUrl,
      getToken: options.getToken,
      peekToken: options.peekToken,
      serverNow: options.serverNow,
      getLocale: options.getLocale,
      getSelfUid: options.getSelfUid,
      foreground: options.foreground,
      dispatch: options.dispatch,
      socketFactory: options.socketFactory,
      log: (...args) => this.log('ChatServerSocketClient', ...args),
    })
    this.socket.listen(event => {
      if (event.type === 'message') void this.handleIncomingMessage(event.channelId, event.message)
      if (event.type === 'participants_changed') void this.handleParticipantsChanged(event.channelId)
      if (event.type === 'retired') this.rebuild(event)
    })
  }

  /** Run the connect lifecycle. Returns a `Disposable` the caller owns. */
  start(): Disposable {
    return spawn(
      async stack => {
        const ab = stack.adopt(new AbortController(), controller => controller.abort())

        stack.use(this.options.register?.(this))
        stack.defer(() => this.options.dispatch(setConnected(false)))
        stack.defer(() => this.options.dispatch(setConnectFailed(false)))

        await this.options.waitUntilReady?.(ab.signal)
        ab.signal.throwIfAborted()

        if (this.options.shouldConnect && !this.options.shouldConnect()) {
          this.log('Skip connect (shouldConnect=false)')
          return
        }

        stack.use(this.options.onActivate?.())

        stack.defer(() => this.disconnect())

        await this.connectUntilOpen(ab.signal)

        ab.signal.throwIfAborted()
        const loadInitialChannels = this.options.loadInitialChannels
        if (loadInitialChannels) {
          try {
            await retry(() => loadInitialChannels(ab.signal), {
              interval: e => (e instanceof ChatServerRateLimitError ? Math.max(0, new Date(e.retryTimestamp).getTime() - Date.now()) : LOAD_RETRY_INTERVAL),
              attempts: 3,
              signal: ab.signal,
              retryable: e => !(e instanceof ChatServerRestError) || !e.isPermanent,
            })
          } catch (e) {
            this.options.reportError?.(e, { op: 'loadInitialChannels' })
          }
        }
      },
      e => this.options.reportError?.(e, { op: 'start' })
    )
  }

  /** Connect the socket directly, bypassing the `start()` lifecycle gate (readiness wait, `shouldConnect`, initial loads). */
  async connect(signal: AbortSignal = new AbortController().signal): Promise<void> {
    await this.socket.connect(signal)
  }

  /**
   * Try again now rather than on the retry timer: cut the wait before the next connect attempt
   * short, or give up one stuck on a network that is gone. A socket that dropped after opening
   * is rebuilt from scratch, since phoenix's own reconnect has no timeout either.
   */
  retryConnect(): void {
    // Trying now: the failure on screen is over until this attempt has had its say.
    this.options.dispatch(setConnectFailed(false))
    if (this.connectAttempt) {
      this.connectAttempt.abort()
    } else if (!this.socket.isConnected) {
      this.socket.retire('retry asked while the socket was down')
    }
  }

  /** Tear the socket down, and the loop rebuilding it if there is one; `start()`'s disposable runs this on teardown. */
  disconnect(): void {
    if (this.rebuilding) dispose(this.rebuilding)
    this.rebuilding = null
    this.socket.disconnect()
  }

  listen(listener: ChatServerManagerListener): () => void {
    return this.socket.listen(listener)
  }

  get isConnected(): boolean {
    return this.socket.isConnected
  }

  /** Prolong the live socket's auth in-band (`auth:refresh` with a freshly fetched token); the socket is rebuilt when the push can't go through. */
  refreshAuth(): Promise<void> {
    return this.socket.refreshAuth()
  }

  /** Subscribe to a channel's realtime topic (`channel:<id>`). */
  joinChannel(channelId: string) {
    return this.socket.joinChannel(channelId)
  }

  leaveChannel(channelId: string): void {
    this.socket.leaveChannel(channelId)
  }

  /** Optimistic send over WS with a client id to reconcile the echo. */
  sendMessage(channelId: string, body: string, attributes: Record<string, unknown> = {}, opts?: ChatServerSendOptions): ChatServerSendResult {
    return this.socket.sendMessage(channelId, body, attributes, opts)
  }

  markRead(channelId: string): void {
    this.socket.markRead(channelId)
  }

  /** Optimistic toggle of the caller's own reaction; server reply reconciles or rolls back. */
  toggleReaction(channelId: string, messageId: string, emoji: string, mine: boolean): void {
    this.socket.toggleReaction(channelId, messageId, emoji, mine)
  }

  /** Press an inline-keyboard button over WS; rejects with `ChatServerButtonClickError`. */
  clickButton(channelId: string, messageId: string, callbackData: string): Promise<void> {
    return this.socket.clickButton(channelId, messageId, callbackData)
  }

  typing(channelId: string): void {
    this.socket.typing(channelId)
  }

  /** Backfill the channel for an inbound message, then notify — load failures are reported, not fatal. */
  private async handleIncomingMessage(channelId: string, message: ChatServerMessage): Promise<void> {
    try {
      await this.options.ensureChannelLoaded?.(channelId)
    } catch (e) {
      this.options.reportError?.(e, { op: 'ensureChannelLoaded' })
    }
    this.maybeNotify(channelId, message)
  }

  /** Reload the channel so its participants follow the join/leave — load failures are reported, not fatal. */
  private async handleParticipantsChanged(channelId: string): Promise<void> {
    try {
      await this.options.refreshChannel?.(channelId)
    } catch (e) {
      this.options.reportError?.(e, { op: 'refreshChannel' })
    }
  }

  /** Bring a retired socket back through the retry loop, and the topics it had joined with it. */
  private rebuild({ reason, rejoin }: { reason: string; rejoin: string[] }): void {
    // A loop already running brings the socket back, and its own rejoin list stands.
    if (this.connectAttempt) return
    this.log('rebuilding the socket', reason)
    this.rebuilding = spawn(
      async stack => {
        const ab = stack.adopt(new AbortController(), controller => controller.abort())
        await this.connectUntilOpen(ab.signal)
        for (const channelId of rejoin) this.socket.joinChannel(channelId)
      },
      e => this.options.reportError?.(e, { op: 'rebuild' })
    )
  }

  /**
   * Connect, retrying every `CONNECT_RETRY_INTERVAL` until the socket opens. Each failure flags
   * the connect as failed for the UI; `retryConnect()` aborts the attempt in hand, which skips
   * the wait rather than counting as a failure.
   */
  private async connectUntilOpen(signal: AbortSignal): Promise<void> {
    let failures = 0
    while (true) {
      signal.throwIfAborted()
      using stack = new DisposableStack()
      const attempt = stack.adopt(new AbortController(), controller => controller.abort())
      const abortAttempt = () => attempt.abort()
      signal.addEventListener('abort', abortAttempt, { once: true })
      stack.defer(() => signal.removeEventListener('abort', abortAttempt))
      this.connectAttempt = attempt
      stack.defer(() => {
        this.connectAttempt = null
      })

      try {
        await this.socket.connect(attempt.signal)
        this.options.dispatch(setConnectFailed(false))
        return
      } catch (e) {
        signal.throwIfAborted()
        if (attempt.signal.aborted) continue

        // Offline, no socket opens: only failures on a network count towards the report.
        if (this.options.isOnline?.() !== false) failures++
        this.log('connect failed', { failures }, e)
        this.options.dispatch(setConnectFailed(true))
        if (failures === REPORTED_CONNECT_FAILURES) {
          this.options.reportError?.(new Error(`ChatServerManager: socket did not open in ${failures} attempts`, { cause: e }), { op: 'connect' })
        }
      }

      await sleep(CONNECT_RETRY_INTERVAL, attempt.signal).catch(() => signal.throwIfAborted())
    }
  }

  private maybeNotify(channelId: string, message: ChatServerMessage): void {
    const senderExternalId = message.sender?.external_id ?? null
    const isIncoming = !!senderExternalId && senderExternalId !== this.options.getSelfUid()
    if (!isIncoming) return
    if (this.options.isChannelActive?.(channelId)) return
    this.options.onNotify?.(message, channelId)
  }

  private log(...args: unknown[]): void {
    this.options.log?.(...args)
  }
}

const LOAD_RETRY_INTERVAL = 10000

const CONNECT_RETRY_INTERVAL = 10000

/** Consecutive failed connects before the host hears of it — once per outage, not per attempt. */
const REPORTED_CONNECT_FAILURES = 3
